import { useEffect } from "react";

import { signOutClient } from "@/lib/signOut";
import { isSessionIdle, touchActivity } from "@/lib/sessionActivity";
import { supabase } from "@/lib/supabase";

const CHECK_INTERVAL_MS = 60_000;

/** Scroll on nested overflow containers does not bubble; wheel/pointer events do. */
const ACTIVITY_EVENTS = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart"] as const;

async function signOutIfIdle(): Promise<void> {
  if (!supabase || !isSessionIdle()) return;

  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) return;

  await signOutClient();
  window.location.replace("/login");
}

/** Tracks user activity and signs out after the configured idle period. */
export function useInactivityTimeout(): void {
  useEffect(() => {
    if (!supabase) return;

    function onActivity(): void {
      touchActivity();
    }

    function onVisibilityChange(): void {
      if (document.visibilityState !== "visible") return;
      if (isSessionIdle()) {
        void signOutIfIdle();
        return;
      }
      touchActivity();
    }

    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, onActivity, { passive: true });
    }
    document.addEventListener("visibilitychange", onVisibilityChange);

    const intervalId = window.setInterval(() => {
      void signOutIfIdle();
    }, CHECK_INTERVAL_MS);

    return () => {
      for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, onActivity);
      }
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, []);
}
